import { useEffect, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { APP_LOCALE } from "../../config/locale";
import { getAuditLogs, type AuditLog } from "../../auditoria/auditoriaApi";

const DEFAULT_TAKE = 100;

const dateTimeFormatter = new Intl.DateTimeFormat(APP_LOCALE, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return dateTimeFormatter.format(date);
}

const ENTITY_LABELS: Record<string, string> = {
  Client: "Cliente",
  Supplier: "Fornecedor",
  Subcontractor: "Subempreiteiro",
  Work: "Obra",
  WorkFinancialEntry: "Fatura",
  AiSettings: "Configurações de IA",
};

function entityLabel(entityName: string): string {
  return ENTITY_LABELS[entityName] ?? entityName;
}

function OperationBadge({ operation }: { operation: string }) {
  let className: string;
  let label: string;
  switch (operation) {
    case "Created":
      className = "bg-emerald-500/10 text-emerald-700 border-emerald-500/30";
      label = "Criado";
      break;
    case "Deleted":
      className = "bg-destructive/10 text-destructive border-destructive/30";
      label = "Eliminado";
      break;
    case "Updated":
      className = "bg-amber-500/10 text-amber-700 border-amber-500/30";
      label = "Atualizado";
      break;
    default:
      className = "bg-muted text-secondary-text border-border";
      label = operation;
  }
  return (
    <span className={`inline-flex px-2 py-1 border text-xs ${className}`}>
      {label}
    </span>
  );
}

export function AuditoriaView() {
  const [logs, setLogs] = useState<AuditLog[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAuditLogs(DEFAULT_TAKE)
      .then(setLogs)
      .catch((err) =>
        setError(
          err instanceof Error ? err.message : "Erro ao carregar o registo de auditoria.",
        ),
      );
  }, []);

  return (
    <div className="h-full overflow-auto bg-background">
      <PageHeader
        title="Auditoria"
        subtitle="Registo de alterações relevantes em entidades-chave"
      />

      <div className="px-4 md:px-6 py-6 md:py-8">
        {error ? (
          <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">
            {error}
          </div>
        ) : !logs ? (
          <div className="text-secondary-text py-6">A carregar registo de auditoria…</div>
        ) : (
          <div
            className="bg-card border border-border"
            style={{ borderRadius: "var(--radius-card)" }}
          >
            {logs.length === 0 ? (
              <div className="text-secondary-text px-5 py-6">
                Ainda não existem registos de auditoria.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border text-left text-secondary-text text-sm">
                      <th className="px-4 py-3">Data/Hora</th>
                      <th className="px-4 py-3">Utilizador</th>
                      <th className="px-4 py-3">Entidade</th>
                      <th className="px-4 py-3">Operação</th>
                      <th className="px-4 py-3">Descrição</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr
                        key={log.id}
                        className="border-b border-border last:border-b-0"
                      >
                        <td className="px-4 py-3 text-foreground whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-secondary-text whitespace-nowrap">
                          {log.userName ?? log.userId ?? "Sistema"}
                        </td>
                        <td className="px-4 py-3 text-foreground whitespace-nowrap">
                          {entityLabel(log.entityName)}
                        </td>
                        <td className="px-4 py-3">
                          <OperationBadge operation={log.operation} />
                        </td>
                        <td className="px-4 py-3 text-foreground">{log.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
